import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { theme } from '../theme';
import { useIsMobile } from '../hooks/useMediaQuery';

function Mora({ username, onLogout, allowBack }) {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Paquetes de recarga con descuento (el monto/bono real lo valida el servidor).
  const PACKAGES = [
    { key: '15', dias: 15, precio: 6000, valor: 7500 },
    { key: '30', dias: 30, precio: 10000, valor: 15000 },
  ];

  const startPayment = async (body) => {
    setError('');
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/pay/create-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.message || 'Error al crear el enlace de pago');
        return;
      }
      window.location.href = data.checkout_url;
    } catch {
      setError('Error de conexión. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleRecharge = () => {
    const numAmount = parseInt(amount);
    if (!numAmount || numAmount < 1500) {
      return setError('El monto mínimo es $1.500 pesos');
    }
    if (numAmount > 5000) {
      return setError('El monto máximo es $5.000 pesos');
    }
    startPayment({ amount: numAmount });
  };

  const handlePackage = (key) => startPayment({ package: key });

  return (
    <main style={{ padding: isMobile ? '2rem 1rem' : '3rem 1.5rem', display: 'flex', justifyContent: 'center' }}>
      <div style={{
        backgroundColor: theme.colors.surface,
        borderRadius: '14px',
        border: `2px solid ${allowBack ? theme.colors.primary : '#ef4444'}`,
        padding: isMobile ? '1.75rem 1.25rem' : '2.5rem',
        width: '100%',
        maxWidth: '460px',
        boxShadow: allowBack
          ? `0 4px 24px ${theme.colors.glowSubtle}`
          : '0 4px 32px rgba(239,68,68,0.18)',
        textAlign: 'center',
      }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>
          {allowBack ? '💳' : '⚠️'}
        </div>

        <h1 style={{ color: allowBack ? theme.colors.primary : '#ef4444', fontSize: '1.5rem', marginBottom: '0.6rem' }}>
          {allowBack ? 'Recargar saldo' : 'Cuenta suspendida'}
        </h1>

        <p style={{ color: theme.colors.textMuted, fontSize: '0.9rem', lineHeight: '1.7', marginBottom: '1.5rem' }}>
          {allowBack
            ? `Hola ${username}, ingresa el monto que deseas recargar.`
            : `Hola ${username}, tu cuenta está suspendida por saldo insuficiente. Recarga para continuar.`}
        </p>

        <div style={{ textAlign: 'left', marginBottom: '1.4rem' }}>
          <label style={{ display: 'block', color: theme.colors.textMuted, fontSize: '0.85rem', marginBottom: '0.6rem' }}>
            Paquetes de recarga
          </label>
          <div style={{ display: 'grid', gap: '0.7rem' }}>
            {PACKAGES.map((p) => (
              <button
                key={p.key}
                onClick={() => handlePackage(p.key)}
                disabled={loading}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '0.75rem',
                  padding: '12px 14px',
                  backgroundColor: theme.colors.background,
                  border: `1px solid ${theme.colors.primary}`,
                  borderRadius: '9px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  textAlign: 'left',
                }}
              >
                <span>
                  <span style={{ display: 'block', color: theme.colors.text, fontWeight: 700, fontSize: '0.95rem' }}>
                    Paquete {p.dias} días
                  </span>
                  <span style={{ display: 'block', color: theme.colors.textMuted, fontSize: '0.78rem' }}>
                    Pagas ${p.precio.toLocaleString('es-CO')} · recibes ${p.valor.toLocaleString('es-CO')} de saldo
                  </span>
                </span>
                <span style={{ color: theme.colors.primary, fontWeight: 700, fontSize: '1.05rem', whiteSpace: 'nowrap' }}>
                  ${p.precio.toLocaleString('es-CO')}
                </span>
              </button>
            ))}
          </div>
          <p style={{ color: theme.colors.textMuted, fontSize: '0.72rem', marginTop: '0.5rem', fontStyle: 'italic' }}>
            ⚠️ Los paquetes aplican solo al plan de Página Web ($500/día), no al plan de WhatsApp ($2.000/día).
          </p>
        </div>

        <div style={{ textAlign: 'left', marginBottom: '1.2rem' }}>
          <label style={{ display: 'block', color: theme.colors.textMuted, fontSize: '0.85rem', marginBottom: '0.4rem' }}>
            O recarga el monto que quieras (COP)
          </label>
          <p style={{ color: theme.colors.textMuted, fontSize: '0.78rem', margin: '0 0 0.4rem' }}>
            Valor entre $1.500 y $5.000 pesos.
          </p>
          <input
            type="number"
            onWheel={e => e.currentTarget.blur()}
            value={amount}
            onChange={e => {
              const v = e.target.value;
              if (v === '') return setAmount('');
              const n = parseInt(v);
              if (Number.isNaN(n)) return;
              setAmount(String(Math.min(n, 5000)));
            }}
            placeholder="ej: 3000"
            min="1500"
            max="5000"
            style={{
              width: '100%',
              padding: '10px 14px',
              backgroundColor: theme.colors.background,
              border: `1px solid ${theme.colors.border}`,
              borderRadius: '7px',
              color: theme.colors.text,
              fontSize: '1rem',
              boxSizing: 'border-box',
              outline: 'none',
            }}
          />
          <p style={{ color: theme.colors.textMuted, fontSize: '0.78rem', marginTop: '0.4rem' }}>
            Mínimo $1.500 · máximo $5.000 pesos
          </p>
        </div>

        {error && (
          <p style={{ color: '#ef4444', fontSize: '0.88rem', marginBottom: '1rem' }}>
            {error}
          </p>
        )}

        <button
          onClick={handleRecharge}
          disabled={loading}
          style={{
            width: '100%',
            padding: '12px',
            backgroundColor: loading ? theme.colors.border : theme.colors.primary,
            color: theme.colors.background,
            border: 'none',
            borderRadius: '7px',
            fontSize: '1rem',
            fontWeight: '700',
            cursor: loading ? 'not-allowed' : 'pointer',
            marginBottom: '1rem',
          }}
        >
          {loading ? 'Procesando...' : 'Continuar con Wompi ▶'}
        </button>

        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
          {allowBack && (
            <button
              onClick={() => navigate(-1)}
              style={{
                padding: '9px 20px',
                backgroundColor: 'transparent',
                color: theme.colors.primary,
                border: `1px solid ${theme.colors.primary}`,
                borderRadius: '7px',
                fontSize: '0.9rem',
                cursor: 'pointer',
              }}
            >
              ← Volver
            </button>
          )}
          <button
            onClick={onLogout}
            style={{
              padding: '9px 20px',
              backgroundColor: 'transparent',
              color: theme.colors.textMuted,
              border: `1px solid ${theme.colors.border}`,
              borderRadius: '7px',
              fontSize: '0.9rem',
              cursor: 'pointer',
            }}
          >
            Cerrar sesión
          </button>
        </div>
      </div>
    </main>
  );
}

export default Mora;
